/**
 * 1.4.0 asset groups (series and seasons): the contracts the three streams share (docs/dev/groups-1.4.0-build.md).
 *
 * Group types (Series, Season, …) are configured by administrators; a group is of one type and sits under a group
 * of the type's parent type; an asset is in at most one group per type. Groups carry their own metadata fields
 * (FieldDefinitionSchema, the asset model's shape) and members can inherit chosen group fields (never over their own
 * value). Schema: migrations 0095 (tables, triggers, defaults, permission group.manage) and 0096 (index).
 */
import { z } from 'zod';
import { FieldDefinitionSchema } from './metadata.ts';
import { DeliveryAssignmentCreate, patchOf, type AssignmentState } from './portal.ts';
import type { AssetSummary } from './assets.ts';

// ---------------------------------------------------------------- errors

/** Error codes of the group routes ({error: {code, message, details?}}), with their HTTP status in GROUP_ERROR_STATUS. */
export const GROUP_ERRORS = {
  /** 409: an asset already in another group of the same type; details GroupTypeTakenDetails. Send move: true to move it. */
  GROUP_TYPE_TAKEN: 'GROUP_TYPE_TAKEN',
  /** 409: deleting a group that has child groups without choosing what happens to them (children: move_up | delete). */
  GROUP_HAS_CHILDREN: 'GROUP_HAS_CHILDREN',
  /** 422: a parent group not of the type's parent type (or a group type's parent chain that would loop). */
  GROUP_WRONG_PARENT_TYPE: 'GROUP_WRONG_PARENT_TYPE',
  /** 409: deleting or archiving a group type, or changing its parent type, while groups of it exist. */
  GROUP_TYPE_IN_USE: 'GROUP_TYPE_IN_USE',
} as const;
export type GroupErrorCode = keyof typeof GROUP_ERRORS;
export const GROUP_ERROR_STATUS: Record<GroupErrorCode, number> = {
  GROUP_TYPE_TAKEN: 409,
  GROUP_HAS_CHILDREN: 409,
  GROUP_WRONG_PARENT_TYPE: 422,
  GROUP_TYPE_IN_USE: 409,
};
/**
 * The database's refusals (0095's triggers raise check_violation with these constraint names, and the unique
 * index on asset_group_members (asset_id, type_id)), as the API's codes. Map pg errors through this (err.constraint).
 */
export const GROUP_CONSTRAINT_ERRORS: Record<string, { code: string; status: number }> = {
  asset_groups_parent_type: { code: GROUP_ERRORS.GROUP_WRONG_PARENT_TYPE, status: 422 },
  asset_groups_type_fixed: { code: 'VALIDATION_FAILED', status: 422 },
  group_types_parent: { code: GROUP_ERRORS.GROUP_WRONG_PARENT_TYPE, status: 422 },
  group_types_in_use: { code: GROUP_ERRORS.GROUP_TYPE_IN_USE, status: 409 },
  asset_group_members_asset_id_type_id_key: { code: GROUP_ERRORS.GROUP_TYPE_TAKEN, status: 409 },
  asset_group_members_live_asset: { code: 'NOT_FOUND', status: 404 },
  asset_group_members_group_live: { code: 'NOT_FOUND', status: 404 },
};

// ---------------------------------------------------------------- limits

/** Assets per members request (add, remove, order): as a collection's items. */
export const GROUP_MEMBERS_MAX = 500;
/** Placeholders one group assignment takes (P2's batch limit of 20 does not apply to groups), and its batch size. */
export const GROUP_ASSIGNMENT_LIMIT = 1000;
export const GROUP_ASSIGNMENT_BATCH = 100;
/** Ticking a group row in the Grouped view asks first above this many matching assets. */
export const GROUP_SELECT_CONFIRM = 200;
/** The Grouped view's page size per level. */
export const GROUPED_PAGE_SIZE = 50;

// ---------------------------------------------------------------- group types

export const GROUP_NUMBERING = ['none', 'number', 'number_in_parent'] as const;
export type GroupNumbering = (typeof GROUP_NUMBERING)[number];
export const GROUP_TYPE_KEY = /^[a-z][a-z0-9_]{0,39}$/;
/** The default types 0095 seeds in every workspace (administrators may change them). */
export const DEFAULT_GROUP_TYPE_KEYS = ['series', 'season'] as const;

const FieldId = z.string().regex(/^f_[a-z0-9_]+$/);
/** One inheritance row: the group field's value shows on members whose own assetField is empty; enabled turns it off. */
export const GroupInheritMapping = z.object({ groupField: FieldId, assetField: FieldId, enabled: z.boolean().default(true) });
export type GroupInheritMapping = z.infer<typeof GroupInheritMapping>;

const Label = z.string().trim().min(1).max(80);
export const GroupTypeCreate = z.object({
  key: z.string().regex(GROUP_TYPE_KEY),
  label: Label,
  pluralLabel: Label,
  parentTypeId: z.string().uuid().nullable().default(null),
  memberLabel: Label.default('Asset'),
  numbering: z.enum(GROUP_NUMBERING).default('none'),
  /** The label of a group's own number ("Season number"); null: not shown. */
  numberLabel: Label.nullable().default(null),
  fields: z.array(FieldDefinitionSchema).max(100).default([]),
  inherit: z.array(GroupInheritMapping).max(100).default([]),
  icon: z.string().max(40).nullable().default(null),
  position: z.number().int().min(0).max(1000).optional(),
});
export type GroupTypeCreate = z.infer<typeof GroupTypeCreate>;
/**
 * PATCH /api/v1/group-types/:id: anything but the key. Changing fields or inherit starts the inheritance recompute.
 * patchOf drops the Create defaults (zod 4's .partial() keeps them, so an omitted field would reset the stored value).
 */
export const GroupTypePatch = patchOf(GroupTypeCreate.omit({ key: true })).extend({ archived: z.boolean().optional() });
export type GroupTypePatch = z.infer<typeof GroupTypePatch>;

export interface GroupTypeDTO {
  id: string;
  key: string;
  label: string;
  pluralLabel: string;
  parentTypeId: string | null;
  memberLabel: string;
  numbering: GroupNumbering;
  numberLabel: string | null;
  fields: z.infer<typeof FieldDefinitionSchema>[];
  inherit: GroupInheritMapping[];
  icon: string | null;
  position: number;
  archived: boolean;
  /** How many groups of the type exist (deleting needs 0). */
  groupCount: number;
  createdAt: string;
  updatedAt: string;
}
export interface GroupTypeListDTO {
  items: GroupTypeDTO[];
  /** The inheritance recompute's status line (Administration), null when none ran yet. */
  recompute: GroupRecomputeStatus | null;
}
export interface GroupRecomputeStatus {
  state: 'queued' | 'running' | 'done' | 'failed';
  done: number;
  total: number;
  startedAt: string | null;
  finishedAt: string | null;
  error: string | null;
}

// ---------------------------------------------------------------- groups

/** A group as an asset or a breadcrumb names it. */
export interface GroupCrumb { id: string; title: string; typeId: string; typeLabel: string; number: number | null }

export interface AssetGroupDTO {
  id: string;
  typeId: string;
  type: { key: string; label: string; memberLabel: string; numbering: GroupNumbering; numberLabel: string | null };
  parentId: string | null;
  title: string;
  description: string;
  number: number | null;
  metadata: Record<string, unknown>;
  posterUrl: string | null;
  posterAssetId: string | null;
  /** The group's own members the caller may see (restricted assets left out), and with its descendants'. */
  memberCount: number;
  descendantMemberCount: number;
  childCount: number;
  createdAt: string;
  updatedAt: string;
}
/** GET /api/v1/groups/:id. */
export interface AssetGroupDetailDTO extends AssetGroupDTO {
  /** From the top-level group down to the parent. */
  path: GroupCrumb[];
  children: AssetGroupDTO[];
  /** The group page's delivery summary (G2, "8 of 12 accepted"); null without portal.assign / portal.review. */
  deliveries: GroupDeliverySummary | null;
}
export interface GroupDeliverySummary { placeholders: number; open: number; uploaded: number; inReview: number; accepted: number }

/** GET /api/v1/groups?type=&parent=&q=&cursor=&limit= (keyset paging). parent: root, or a group id. */
export const GroupListQuery = z.object({
  type: z.string().uuid().optional(),
  parent: z.union([z.literal('root'), z.string().uuid()]).optional(),
  q: z.string().max(200).optional(),
  cursor: z.string().max(400).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});
export type GroupListQuery = z.infer<typeof GroupListQuery>;
export interface GroupListDTO { items: AssetGroupDTO[]; nextCursor: string | null }

export const GroupCreate = z.object({
  typeId: z.string().uuid(),
  parentId: z.string().uuid().nullable().default(null),
  title: z.string().trim().min(1).max(300),
  description: z.string().max(20000).default(''),
  number: z.number().int().min(0).max(1_000_000).nullable().default(null),
  /** Validated against the type's fields (validateMetadata). */
  metadata: z.record(z.string(), z.unknown()).default({}),
  posterAssetId: z.string().uuid().nullable().default(null),
  /** Members to add at once (Add to group… → New Series…), as POST …/members. */
  assetIds: z.array(z.string().uuid()).max(GROUP_MEMBERS_MAX).default([]),
  move: z.boolean().default(false),
});
export type GroupCreate = z.infer<typeof GroupCreate>;
export const GroupPatch = z.object({
  parentId: z.string().uuid().nullable(),
  title: z.string().trim().min(1).max(300),
  description: z.string().max(20000),
  number: z.number().int().min(0).max(1_000_000).nullable(),
  metadata: z.record(z.string(), z.unknown()),
  posterAssetId: z.string().uuid().nullable(),
}).partial();
export type GroupPatch = z.infer<typeof GroupPatch>;
/** DELETE /api/v1/groups/:id?children=move_up|delete: without it, a group with children is 409 GROUP_HAS_CHILDREN. */
export const GroupDeleteQuery = z.object({ children: z.enum(['move_up', 'delete']).optional() });
export type GroupDeleteQuery = z.infer<typeof GroupDeleteQuery>;

// ---------------------------------------------------------------- members

/** POST /api/v1/groups/:id/members. position: where the first goes (the rest follow); omitted, at the end. */
export const GroupMembersAdd = z.object({
  assetIds: z.array(z.string().uuid()).min(1).max(GROUP_MEMBERS_MAX),
  position: z.number().optional(),
  /** Move assets already in another group of this type (else 409 GROUP_TYPE_TAKEN). */
  move: z.boolean().default(false),
});
export type GroupMembersAdd = z.infer<typeof GroupMembersAdd>;
/** GROUP_TYPE_TAKEN's details: each asset with the group of the same type it is in now (title: that group's title). */
export interface GroupTypeTakenDetails { assets: Array<{ assetId: string; groupId: string; title: string }> }
export interface GroupMembersAddResult { added: string[]; moved: string[]; refused: Array<{ assetId: string; code: string; message: string }> }
/** POST /api/v1/groups/:id/members/remove. */
export const GroupMembersRemove = z.object({ assetIds: z.array(z.string().uuid()).min(1).max(GROUP_MEMBERS_MAX) });
export type GroupMembersRemove = z.infer<typeof GroupMembersRemove>;
/** PATCH /api/v1/groups/:id/members/order: the members in their new order; renumber: numbers 1…n in that order. */
export const GroupMembersOrder = z.object({
  assetIds: z.array(z.string().uuid()).min(1).max(5000),
  renumber: z.boolean().default(false),
});
export type GroupMembersOrder = z.infer<typeof GroupMembersOrder>;
/** PATCH /api/v1/groups/:id/members/:assetId. */
export const GroupMemberPatch = z.object({ number: z.number().int().min(0).max(1_000_000).nullable() });
export type GroupMemberPatch = z.infer<typeof GroupMemberPatch>;
/** GET /api/v1/groups/:id/members?cursor=: the members the caller may see, in their order. */
export interface GroupMemberDTO {
  asset: AssetSummary;
  position: number;
  number: number | null;
  /** The placeholder's latest assignment (G2's column); null when none or without portal.assign / portal.review. */
  delivery: { assignmentId: string; state: AssignmentState } | null;
}
export interface GroupMembersPageDTO { items: GroupMemberDTO[]; nextCursor: string | null }

// ---------------------------------------------------------------- the asset's side (AssetSummary.groups, inheritedMetadata)

/** One of an asset's groups: "Series › Season 1 – Murder House · Episode 3". */
export interface AssetGroupRef {
  typeId: string;
  typeKey: string;
  typeLabel: string;
  memberLabel: string;
  groupId: string;
  title: string;
  /** The group's own number (the season number). */
  groupNumber: number | null;
  /** The asset's number in the group (the episode number). */
  number: number | null;
  /** The groups above it, from the top. */
  path: GroupCrumb[];
}
/** A value shown on the asset because its own is empty: from which group (and type) it comes. */
export interface InheritedValue { value: unknown; groupId: string; typeLabel: string }

// ---------------------------------------------------------------- the Grouped view (G3)

/** GET /api/v1/assets/grouped?…the Library's filters…&parent=root|group:<id>&cursor= */
export const GroupedParent = z.union([z.literal('root'), z.string().regex(/^group:[0-9a-f-]{36}$/)]);
export type GroupedParent = z.infer<typeof GroupedParent>;
export type GroupedRow =
  /** matchCount null: not counted in time (a slow or cold database); the Library shows "…". */
  | { kind: 'group'; depth: number; group: AssetGroupDTO; matchCount: number | null; matchCountExact: boolean; hasChildren: boolean }
  | { kind: 'asset'; depth: number; asset: AssetSummary; groupId: string | null; memberNumber: number | null };
export interface GroupedPageDTO {
  rows: GroupedRow[];
  /** Under the root: groups first, then the ungrouped assets (phase 'ungrouped'). */
  nextCursor: string | null;
  phase: 'groups' | 'members' | 'ungrouped';
  /**
   * The part stopped on its budget, or on a statement past its timeout, before a page was found (fewer rows, maybe
   * none): the root's scan of the series, the walk of the ungrouped assets, or any level on a slow database.
   * nextCursor continues from where it stopped (the Library says "Searching more…" and asks for it at once, until
   * the person presses Stop or leaves).
   */
  searching?: boolean;
}

// ---------------------------------------------------------------- assigning a whole group for delivery (G2)

/**
 * POST /api/v1/assignments with groupId (portal.assign): every placeholder of the group and its sub-groups, up to
 * GROUP_ASSIGNMENT_LIMIT, in batches of GROUP_ASSIGNMENT_BATCH (each its own transaction). preview: only the counts
 * and outcomes, nothing made (the dialog's "12 placeholders will be assigned; 3 already have media; 1 is already
 * assigned"). onlyUnassigned: "Assign the unassigned…" (members without an assignment that is not cancelled).
 */
export const GroupAssignmentCreate = DeliveryAssignmentCreate.omit({ assetIds: true }).extend({
  groupId: z.string().uuid(),
  preview: z.boolean().default(false),
  onlyUnassigned: z.boolean().default(false),
});
export type GroupAssignmentCreate = z.infer<typeof GroupAssignmentCreate>;
export const GROUP_ASSIGNMENT_OUTCOMES = ['created', 'would_create', 'has_media', 'already_assigned', 'refused'] as const;
export type GroupAssignmentOutcomeKind = (typeof GROUP_ASSIGNMENT_OUTCOMES)[number];
export interface GroupAssignmentOutcome {
  assetId: string;
  assetCode: string;
  title: string;
  groupId: string;
  outcome: GroupAssignmentOutcomeKind;
  assignmentId: string | null;
  code?: string;
  message?: string;
}
export interface GroupAssignmentResult {
  groupId: string;
  preview: boolean;
  counts: Record<GroupAssignmentOutcomeKind, number>;
  members: GroupAssignmentOutcome[];
}
/** The supplier's My deliveries (G3): a delivery's group, shown as "Series › Season" in episode order. */
export interface PortalDeliveryGroup { groupId: string; path: string[]; number: number | null }

/** Whether a body sent to POST /api/v1/assignments is a group assignment. */
export function isGroupAssignmentBody(body: unknown): boolean {
  return typeof body === 'object' && body !== null && typeof (body as { groupId?: unknown }).groupId === 'string';
}
